import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBrandList } from 'store/server/useBrand'
import { FiUpload, FiFileText, FiX, FiSettings } from 'react-icons/fi'
import { HiOutlineDocumentDuplicate } from 'react-icons/hi'
import { IoCheckmarkCircle } from 'react-icons/io5'

import MainTitle from 'components/MainTitle'
import Spinner from 'components/spinner'
import { BASE_URL2 } from 'config/axios'
import requests from 'service/api'
import { toast } from 'components/ui/use-toast'

export default function Updates() {
  const { data, isFetching } = useBrandList()
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null)
  const navigate = useNavigate()

  const normalizeBrandName = (brandName) => {
    return brandName.toLowerCase().replace(/\s+/g, '-')
  }

  const handleBedenGrubuYonet = () => {
    if (!selectedBrand) return
    const normalizedBrand = normalizeBrandName(selectedBrand.name)
    navigate(`/güncellemeler/${normalizedBrand}/size-groups`)
  }

  if (isFetching) return <Spinner className="mt-20" />

  const handleBrandSelect = (brand) => {
    setSelectedBrand(selectedBrand?.id === brand.id ? null : brand)
  }

  const handleBedenGrubuUpdate = () => {
    if (!selectedBrand) return
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!selectedBrand) {
      toast.error('Lütfen önce bir marka seçin')
      return
    }

    // Dosya tipi kontrolü
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ]
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Lütfen geçerli bir Excel dosyası seçin (.xlsx, .xls, .csv)')
      event.target.value = ''
      return
    }

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('brand', selectedBrand.name.toLowerCase().replace(/\s+/g, ''))

      const params = {
        update: 'true', // Update mode aktif
        test: 'true' // Test mode kapalı
      }

      const response = await requests.order.uploadSizeGroupExcel(formData, params)

      if (response.data) {
        const { result, brand: responseBrand } = response.data
        toast.success(
          `Beden Grubu güncelleme başarılı! ${result.created} eklendi, ${result.updated} güncellendi.`
        )
      }
    } catch (error) {
      console.error('Upload hatası:', error)
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Dosya yüklenirken bir hata oluştu'
      toast.error(errorMessage)
    } finally {
      setIsUploading(false)
      event.target.value = '' // Input'u temizle
    }
  }

  return (
    <div className="relative">
      {/* Overlay - Seçili marka varsa ekranı karart */}
      {selectedBrand && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity duration-300"
          onClick={() => setSelectedBrand(null)}
        />
      )}

      <div className={`relative z-50 transition-all duration-300 ${selectedBrand ? 'pointer-events-auto' : ''}`}>
        <div className="mb-5">
          <MainTitle>Güncellenecek Marka Seçin</MainTitle>
        </div>
        
        <div className="grid grid-cols-3 gap-5 mb-8">
          {data.map((brand) => (
            <div
              key={brand.id}
              onClick={() => handleBrandSelect(brand)}
              className={`relative grid bg-gray-100 rounded-lg p-5 items-center border-2 border-dashed transition-all duration-300 ease-in-out cursor-pointer ${
                selectedBrand?.id === brand.id
                  ? 'border-blue-500 border-opacity-100 bg-gradient-to-br from-blue-100 to-blue-200 shadow-xl scale-105 z-50'
                  : selectedBrand
                    ? 'opacity-40 border-gray-400 border-opacity-0'
                    : 'border-gray-400 border-opacity-0 hover:border-opacity-100 hover:bg-gray-50'
              }`}
            >
              {/* Seçili marka için onay işareti */}
              {selectedBrand?.id === brand.id && (
                <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1 shadow-lg">
                  <IoCheckmarkCircle className="w-5 h-5 text-white" />
                </div>
              )}
              
              <div className="justify-self-start w-full">
                <div className={`font-semibold ${selectedBrand?.id === brand.id ? 'text-blue-700' : 'text-s-blue'}`}>
                  <div>{brand?.name}</div>
                </div>
              </div>
              <div className="justify-self-center">
                <img 
                  src={`${BASE_URL2}${brand?.logoPath}`} 
                  className={`h-10 transition-all duration-300 ${selectedBrand?.id === brand.id ? 'brightness-110' : ''}`} 
                  alt="marka ismi" 
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedBrand && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          <div 
            className="bg-white rounded-xl shadow-2xl border-2 border-blue-200 p-6 max-w-2xl w-full pointer-events-auto transform transition-all duration-300 scale-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Kapat butonu */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-1">
                  {selectedBrand.name} - Güncelleme İşlemleri
                </h3>
                <p className="text-sm text-gray-500">Excel dosyası yükleyerek güncelleme yapabilirsiniz</p>
              </div>
              <button
                onClick={() => setSelectedBrand(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                title="Kapat"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isUploading}
                />
                <button
                  onClick={handleBedenGrubuUpdate}
                  disabled={isUploading}
                  className="group relative flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 w-full"
                >
                  {isUploading ? (
                    <>
                      <div className="mb-3 p-3 bg-blue-500 rounded-full animate-pulse">
                        <FiUpload className="w-6 h-6 text-white animate-spin" />
                      </div>
                      <span className="font-semibold text-gray-800 mb-1">Yükleniyor...</span>
                      <span className="text-xs text-gray-500 text-center">Lütfen bekleyin</span>
                    </>
                  ) : (
                    <>
                      <div className="mb-3 p-3 bg-blue-500 rounded-full group-hover:bg-blue-600 transition-colors">
                        <HiOutlineDocumentDuplicate className="w-6 h-6 text-white" />
                      </div>
                      <span className="font-semibold text-gray-800 mb-1">Beden Grubu Güncelle</span>
                      <span className="text-xs text-gray-500 text-center">Excel dosyası ile beden gruplarını güncelleyin</span>
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <FiUpload className="w-4 h-4 text-blue-500" />
                      </div>
                    </>
                  )}
                </button>
              </div>
              
              <div className="relative">
                <button
                  onClick={handleBedenGrubuYonet}
                  className="group relative flex flex-col items-center justify-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border-2 border-green-200 hover:border-green-400 transition-all duration-300 hover:shadow-lg hover:scale-105 w-full"
                >
                  <div className="mb-3 p-3 bg-green-500 rounded-full group-hover:bg-green-600 transition-colors">
                    <FiSettings className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800 mb-1">Beden Grubu Yönet</span>
                  <span className="text-xs text-gray-500 text-center">SizeGroup'ları görüntüle, düzenle ve yönet</span>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <FiSettings className="w-4 h-4 text-green-500" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
